import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Heart, Plus, Trash2, Download, Printer, Save, MapPin, Calendar, Sparkles,
  Sun, Camera, Gift, Star, ArrowLeft,
} from "lucide-react";

const STORAGE_KEY = "vp_honeymoon_journal_v1";

const DAY_PROMPTS = [
  { key: "highlight",    label: "Highlight of the day for us as a couple",       placeholder: "What made today truly ours…", icon: Heart },
  { key: "romantic",     label: "Most romantic spot or moment today",            placeholder: "A view, a table, a whispered exchange…", icon: Sparkles },
  { key: "keepsake",     label: "A keepsake, picture or memory to hold onto",    placeholder: "A pressed flower, a receipt, a photo we won't forget…", icon: Camera },
  { key: "surprise",     label: "Surprises or special touches today",            placeholder: "Something one of us did — or something the trip did for us…", icon: Gift },
  { key: "gratitude",    label: "What I'm grateful for about you today",         placeholder: "A soft, honest sentence about your partner…", icon: Star },
];

const TRIP_PROMPTS = [
  { key: "favouriteMoment", label: "Our single favourite moment of the honeymoon" },
  { key: "biggestLaugh",    label: "The moment we laughed the hardest" },
  { key: "quietestMemory",  label: "The quietest, most intimate memory we want to keep forever" },
  { key: "surprisedUs",     label: "Something about each other this trip taught us" },
  { key: "aspirations",     label: "Future aspirations we dreamed up together on this trip" },
  { key: "traditions",      label: "A ritual or tradition we want to bring home from this honeymoon" },
];

const emptyDay = () => ({
  id: Date.now() + Math.random(),
  date: "",
  location: "",
  weather: "",
  responses: {},
});

const defaultState = () => ({
  meta: { coupleNames: "", destination: "", start: "", end: "" },
  days: [emptyDay()],
  trip: {},
  updatedAt: null,
});

function loadFromStorage() {
  try {
    const raw = typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw);
    return { ...defaultState(), ...parsed };
  } catch {
    return defaultState();
  }
}

export default function HoneymoonJournal() {
  const navigate = useNavigate();
  const [state, setState] = useState(defaultState);
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    setState(loadFromStorage());
    setRestored(true);
  }, []);

  // Auto-save every change
  useEffect(() => {
    if (!restored) return;
    try {
      const snapshot = { ...state, updatedAt: new Date().toISOString() };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch (e) {
      console.warn("journal save failed", e);
    }
  }, [state, restored]);

  const dayCount = state.days.length;
  const wordCount = useMemo(() => {
    const parts = [
      ...state.days.flatMap((d) => Object.values(d.responses || {})),
      ...Object.values(state.trip || {}),
    ];
    return parts.join(" ").split(/\s+/).filter(Boolean).length;
  }, [state]);

  const setMeta = (patch) => setState((s) => ({ ...s, meta: { ...s.meta, ...patch } }));
  const setTrip = (key, value) => setState((s) => ({ ...s, trip: { ...s.trip, [key]: value } }));
  const setDay  = (id, patch) => setState((s) => ({ ...s, days: s.days.map((d) => (d.id === id ? { ...d, ...patch } : d)) }));
  const setDayResp = (id, key, value) => setState((s) => ({
    ...s,
    days: s.days.map((d) => (d.id === id ? { ...d, responses: { ...d.responses, [key]: value } } : d)),
  }));
  const addDay = () => setState((s) => ({ ...s, days: [...s.days, emptyDay()] }));
  const removeDay = (id) => setState((s) => {
    if (s.days.length <= 1) { toast.error("Keep at least one day."); return s; }
    return { ...s, days: s.days.filter((d) => d.id !== id) };
  });

  const clearAll = () => {
    if (!window.confirm("Erase this journal? This cannot be undone.")) return;
    const fresh = defaultState();
    setState(fresh);
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) { console.warn(e); }
    toast.success("Journal cleared.");
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `honeymoon-journal-${(state.meta.coupleNames || "couple").replace(/\s+/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded — keep a copy in the cloud too ✨");
  };

  const printJournal = () => { window.print(); };

  return (
    <div className="min-h-screen bg-[#fdf7ef] text-[#3a2f26]" data-testid="honeymoon-journal-page">
      <Helmet>
        <title>Our Honeymoon Journal — Fillable Template for Couples | Vacations Pro</title>
        <meta name="description" content="A romantic, guided in-browser honeymoon journal for newlyweds. Daily highlights, romantic moments, keepsakes and shared reflections. Auto-saved, printable, private to you." />
        <link rel="canonical" href="https://vacationspro.com/honeymoon-journal" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Our Honeymoon Journal — Fillable Template for Couples" />
        <meta property="og:description" content="Guided honeymoon journal with daily romantic prompts, auto-saved in your browser. From Vacations Pro." />
        <meta property="og:url" content="https://vacationspro.com/honeymoon-journal" />
        <meta property="og:image" content="https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200" />
        <meta property="og:site_name" content="Vacations Pro" />
      </Helmet>

      {/* Print styles */}
      <style>{`
        @keyframes hjFade { 0% { opacity: 0; transform: translateY(6px); } 100% { opacity: 1; transform: translateY(0); } }
        .hj-day { animation: hjFade 400ms ease both; }
        @media print {
          .hj-noprint { display: none !important; }
          body, .hj-print-root { background: white !important; color: #1a120b !important; }
          .hj-page-break { page-break-after: always; }
        }
      `}</style>

      <header className="hj-noprint sticky top-0 z-30 bg-[#fdf7ef]/95 backdrop-blur border-b border-[#e9dcc8] px-5 md:px-10 py-3 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="text-[11px] tracking-[0.2em] uppercase text-[#7a6752] hover:text-[#3a2f26] inline-flex items-center gap-2" data-testid="journal-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Home
        </button>
        <div className="flex items-center gap-2">
          <button onClick={exportJson} className="hidden sm:inline-flex items-center gap-1.5 border border-[#c9a75c] text-[#8a6a2f] rounded-full px-4 py-2 text-[11px] tracking-[0.15em] uppercase font-semibold hover:bg-[#c9a75c]/10 transition-colors" data-testid="journal-download">
            <Download className="w-3.5 h-3.5" /> Backup .json
          </button>
          <button onClick={printJournal} className="inline-flex items-center gap-1.5 bg-[#c9a75c] hover:bg-[#b39147] text-white rounded-full px-4 py-2 text-[11px] tracking-[0.15em] uppercase font-semibold transition-colors" data-testid="journal-print">
            <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
          </button>
        </div>
      </header>

      <div className="hj-print-root max-w-[860px] mx-auto px-6 md:px-10 py-10 md:py-16">
        {/* Cover */}
        <section className="text-center">
          <Heart className="w-10 h-10 text-[#c9a75c] mx-auto" strokeWidth={1.4} />
          <div className="text-[11px] tracking-[0.34em] uppercase text-[#8a6a2f] font-semibold mt-4">Our Honeymoon Journal</div>
          <h1 className="font-serif-vp text-5xl md:text-7xl leading-[0.98] mt-4 text-[#3a2f26]">
            A love letter,
            <span className="block italic text-[#a37a3b]">day by day.</span>
          </h1>
          <p className="text-[15px] text-[#7a6752] mt-5 max-w-xl mx-auto leading-relaxed">
            Guided prompts to capture your first married chapter — the highlights, the whispers, the surprises,
            the keepsakes. Auto-saved to this browser. Print it as a book when you get home.
          </p>
          <div className="hj-noprint mt-6 inline-flex items-center gap-2 text-[11px] text-[#8a6a2f] bg-[#f5e6c8]/60 border border-[#e6d0a0] rounded-full px-4 py-1.5">
            <Save className="w-3.5 h-3.5" />
            Auto-saved to this browser · Never shared · {state.updatedAt ? `Last saved ${new Date(state.updatedAt).toLocaleString()}` : "Not saved yet"}
          </div>
        </section>

        {/* Meta */}
        <section className="mt-14 bg-white rounded-2xl border border-[#e9dcc8] p-6 md:p-8 shadow-[0_10px_30px_rgba(163,122,59,0.08)]" data-testid="journal-meta">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#8a6a2f] font-semibold">The Two of Us</div>
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            <JField label="Our Names" placeholder="e.g. Ananya & Vikram" value={state.meta.coupleNames}
                    onChange={(v) => setMeta({ coupleNames: v })} testid="journal-couple" />
            <JField label="Destination" placeholder="e.g. Maldives · Baa Atoll" icon={MapPin}
                    value={state.meta.destination} onChange={(v) => setMeta({ destination: v })} testid="journal-destination" />
            <JField label="Trip Starts" type="date" icon={Calendar} value={state.meta.start}
                    onChange={(v) => setMeta({ start: v })} testid="journal-start" />
            <JField label="Trip Ends" type="date" icon={Calendar} value={state.meta.end}
                    onChange={(v) => setMeta({ end: v })} testid="journal-end" />
          </div>
        </section>

        {/* Days */}
        <section className="mt-14" data-testid="journal-days">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#8a6a2f] font-semibold">Day-by-Day</div>
              <h2 className="font-serif-vp text-3xl md:text-4xl text-[#3a2f26] mt-2 leading-tight">Romantic highlights &amp; shared moments.</h2>
            </div>
            <button onClick={addDay}
                    className="hj-noprint inline-flex items-center gap-1.5 bg-[#3a2f26] hover:bg-[#241c14] text-[#e4c07c] rounded-full px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase font-semibold transition-colors"
                    data-testid="journal-add-day">
              <Plus className="w-3.5 h-3.5" /> Add Another Day
            </button>
          </div>

          <div className="space-y-6">
            {state.days.map((day, idx) => (
              <article key={day.id} className="hj-day bg-white rounded-2xl border border-[#e9dcc8] p-6 md:p-8 shadow-[0_10px_30px_rgba(163,122,59,0.08)]"
                       data-testid={`journal-day-${idx}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="font-serif-vp text-2xl md:text-3xl text-[#3a2f26]">Day {String(idx + 1).padStart(2, "0")}</div>
                  {state.days.length > 1 && (
                    <button onClick={() => removeDay(day.id)}
                            className="hj-noprint text-[11px] text-[#a04d4d] hover:text-[#7c1e1e] inline-flex items-center gap-1"
                            aria-label={`Remove day ${idx + 1}`} data-testid={`journal-remove-day-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  )}
                </div>
                <div className="grid md:grid-cols-3 gap-3 mt-4">
                  <JField label="Date" type="date" icon={Calendar} value={day.date}
                          onChange={(v) => setDay(day.id, { date: v })} testid={`journal-day-${idx}-date`} />
                  <JField label="Where we are" icon={MapPin} value={day.location}
                          placeholder="e.g. Uluwatu, Bali" onChange={(v) => setDay(day.id, { location: v })}
                          testid={`journal-day-${idx}-location`} />
                  <JField label="Weather / Mood" icon={Sun} value={day.weather}
                          placeholder="Sunlit · Golden · Windswept…"
                          onChange={(v) => setDay(day.id, { weather: v })} testid={`journal-day-${idx}-weather`} />
                </div>

                <div className="mt-6 space-y-4">
                  {DAY_PROMPTS.map((p) => {
                    const Icon = p.icon;
                    return (
                      <div key={p.key}>
                        <div className="flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase text-[#8a6a2f] font-semibold">
                          <Icon className="w-3.5 h-3.5" /> {p.label}
                        </div>
                        <textarea
                          value={day.responses?.[p.key] || ""}
                          onChange={(e) => setDayResp(day.id, p.key, e.target.value)}
                          placeholder={p.placeholder}
                          rows={3}
                          className="mt-2 w-full bg-[#fdf7ef] border border-[#e9dcc8] focus:border-[#c9a75c] outline-none rounded-lg px-3 py-2 text-[15px] leading-relaxed font-serif-vp placeholder:text-[#c4b394] resize-y"
                          data-testid={`journal-day-${idx}-${p.key}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </article>
            ))}
          </div>
        </section>

        <div className="hj-page-break" />

        {/* Trip-wide reflection */}
        <section className="mt-14 bg-white rounded-2xl border border-[#e9dcc8] p-6 md:p-8 shadow-[0_10px_30px_rgba(163,122,59,0.08)]"
                 data-testid="journal-trip">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#8a6a2f] font-semibold">Looking Back Together</div>
          <h2 className="font-serif-vp text-3xl md:text-4xl text-[#3a2f26] mt-2 leading-tight">The honeymoon, in one place.</h2>
          <p className="text-sm text-[#7a6752] mt-3 leading-relaxed max-w-2xl">
            Fill these once, on the plane home. Read them together on your first anniversary.
          </p>
          <div className="mt-6 space-y-5">
            {TRIP_PROMPTS.map((p) => (
              <div key={p.key}>
                <div className="text-[11px] tracking-[0.14em] uppercase text-[#8a6a2f] font-semibold">{p.label}</div>
                <textarea value={state.trip?.[p.key] || ""}
                          onChange={(e) => setTrip(p.key, e.target.value)}
                          rows={3}
                          className="mt-2 w-full bg-[#fdf7ef] border border-[#e9dcc8] focus:border-[#c9a75c] outline-none rounded-lg px-3 py-2 text-[15px] leading-relaxed font-serif-vp placeholder:text-[#c4b394] resize-y"
                          data-testid={`journal-trip-${p.key}`} />
              </div>
            ))}
          </div>
        </section>

        {/* Stats */}
        <section className="hj-noprint mt-10 flex items-center justify-between text-[12px] text-[#8a6a2f]">
          <span>{dayCount} day{dayCount === 1 ? "" : "s"} · {wordCount} words written</span>
          <button onClick={clearAll} className="text-[11px] text-[#a04d4d] hover:text-[#7c1e1e] inline-flex items-center gap-1" data-testid="journal-clear">
            <Trash2 className="w-3.5 h-3.5" /> Erase journal
          </button>
        </section>

        {/* CTA back to concierge */}
        <section className="hj-noprint mt-14 bg-[#3a2f26] text-[#f5e6c8] rounded-2xl p-8 md:p-10 text-center">
          <Sparkles className="w-6 h-6 text-[#e4c07c] mx-auto mb-3" strokeWidth={1.4} />
          <h3 className="font-serif-vp text-3xl md:text-4xl leading-tight">Not on your honeymoon yet?</h3>
          <p className="text-white/70 mt-3 text-sm">
            Our VIP concierge curates once-in-a-lifetime honeymoons in the Amalfi Coast, Switzerland, Maldives, Hawaii, Bali &amp; Seychelles.
          </p>
          <button onClick={() => navigate("/honeymoon-experiences")}
                  className="mt-6 inline-flex items-center gap-2 bg-[#e4c07c] hover:bg-[#d0aa66] text-[#3a2f26] rounded-full px-7 py-3.5 text-[12px] tracking-[0.16em] uppercase font-bold transition-colors"
                  data-testid="journal-cta-luxury">
            Explore Luxury Honeymoon Experiences
          </button>
        </section>
      </div>
    </div>
  );
}

function JField({ label, value, onChange, type = "text", placeholder = "", icon: Icon, testid }) {
  return (
    <label className="block border border-[#e9dcc8] rounded-lg px-3 py-2 bg-[#fdf7ef] focus-within:border-[#c9a75c] transition-colors">
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase text-[#8a6a2f] font-semibold">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <input type={type} value={value || ""} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)}
             className="w-full bg-transparent outline-none text-[15px] mt-0.5 placeholder:text-[#c4b394] font-serif-vp"
             data-testid={testid} />
    </label>
  );
}
